/**
 * product-reviews.js
 * Shopify Product Reviews Widget — Vanilla JS
 *
 * Two entry points, both auto-initialise on DOMContentLoaded:
 *   1. ProductReviewWidget  — product page reviews list + submission form
 *   2. PostPurchasePopup    — order-confirmation review popup
 *
 * ─── API INTEGRATION ────────────────────────────────────────────────────────
 * Replace the two functions in the "── API layer ──" section below with real
 * fetch() calls once your backend is ready. The mock implementations here
 * match the expected request/response shapes exactly.
 *
 * GET  /api/reviews?product_id=<id>&page=<n>&limit=<n>
 *   Response: { reviews: Review[], total: number, average: number, distribution: number[5] }
 *
 * POST /api/reviews
 *   Body:     { product_id, author, email, title, body, rating }
 *   Response: { success: true } | { error: string }
 * ────────────────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  /* ── Analytics bridge ───────────────────────────────────────────────────── */
  /**
   * Reports an accepted review submission to analytics.js.
   *
   * This file is loaded as a classic <script>, not an ES module, so it cannot
   * import trackFormSubmit. analytics.js listens for this event instead — see
   * trackBridgedFormSubmits() there.
   *
   * @param {HTMLFormElement} form
   */
  function reportFormSubmit(form) {
    if (!form) return;
    document.dispatchEvent(new CustomEvent('ma:form-submitted', { detail: { form: form } }));
  }

  /* ── Translation helper ─────────────────────────────────────────────────── */
  var T = window.prTranslations || {};
  function t(key, fallback) { return T[key] || fallback; }
  function tReplace(key, replacements, fallback) {
    var str = T[key] || fallback;
    for (var k in replacements) {
      str = str.replace('__' + k.toUpperCase() + '__', replacements[k]);
    }
    return str;
  }

  const API_BASE = "/shopify-app-proxy/product-reviews/v2/prod-review";
  const IMAGE_UPLOAD_BASE = "/shopify-app-proxy/image-uploader/v1/Resource/Image/REVIEW/v2";
  const IMAGE_API_KEY = "4f023a9420874d83b1a9742bb9f31a4c";
  const VIDEO_UPLOAD_URL = "/shopify-app-proxy/product-reviews/v2/prod-review/video";
  const SHOPPER_CID = (document.getElementById("pr-widget") || {}).dataset?.shopperCid || "";

  /* ── AMOS header helper (uses getInterstitialApiLocale exposed by locale-map.js) ── */
  function amosHeaders() {
    const { languageCode, countryCode } = (typeof window.getInterstitialApiLocale === 'function')
      ? window.getInterstitialApiLocale()
      : (console.warn('[product-reviews] locale-map.js not loaded; defaulting to USA/ENG'),
         { languageCode: 'ENG', countryCode: 'USA' });

    return {
      "AMOS-Country":  countryCode,
      "AMOS-Language": languageCode,
      "AMOS-SiteType": "W"
    };
  }


  /** Read the APIM auth token from the meta tag rendered by apim-auth-token.liquid */
  function getApimToken() {
    var meta = document.querySelector('meta[name="apim-auth-token"]');
    return meta ? meta.content : "";
  }

  /* ── Shared utilities ───────────────────────────────────────────────────── */

  /**
   * Focuses the first genuinely fillable field in the review form. The hidden
   * `rating` input is the first <input> in DOM order, so a plain
   * `querySelector('input, textarea')` silently focuses nothing (browsers no-op
   * .focus() on hidden inputs) — this skips it in favor of the star picker.
   */
  function focusFirstFormField(formWrapper) {
    var field = formWrapper.querySelector('.pr-star-picker__star, input:not([type="hidden"]), textarea');
    if (field) field.focus();
  }

  function starSvg(filled) {
    return '<svg viewBox="0 0 20 20" fill="currentColor" class="' + (filled ? '' : 'empty') + '">'
      + '<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 '
      + '1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 '
      + '1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 '
      + '1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>'
      + '</svg>';
  }

  function renderStars(rating) {
    var html = '<span class="pr-stars" aria-label="' + tReplace('starsAria', {rating: rating}, rating + ' out of 5 stars') + '">';
    for (var i = 1; i <= 5; i++) html += starSvg(i <= rating);
    return html + '</span>';
  }

  function renderReviewMedia(r) {
    var images = r.reviewImages || [];
    var videoUrl = r.reviewVideoUrl || '';
    if (!images.length && !videoUrl) return '';

    var html = '<div class="pr-card__media">';

    if (images.length) {
      html += '<div class="pr-card__media-images">';
      images.forEach(function (img, i) {
        html += '<button class="pr-card__media-link" type="button" data-full-url="' + escHtml(img.mainImageUrl) + '" data-review-img-index="' + i + '">'
          + '<img class="pr-card__media-img" src="' + escHtml(img.thumbnailImageUrl) + '" alt="' + escHtml(t('reviewImageAlt', 'Review image')) + '"'
          + ' width="' + (img.width ? Math.min(img.width, 216) : 216) + '"'
          + ' loading="lazy" />'
          + '</button>';
      });
      html += '</div>';
    }

    if (videoUrl) {
      html += '<div class="pr-card__media-video">'
        + '<video class="pr-card__media-player" controls preload="metadata" playsinline>'
        + '<source src="' + escHtml(videoUrl) + '" type="video/mp4" />'
        + t('videoNotSupported', 'Your browser does not support the video tag.')
        + '</video>'
        + '</div>';
    }

    html += '</div>';
    return html;
  }

  /* ── API layer ──────────────────────────────────────────────────────────── */

  /**
   * Fetch reviews from the v2 API.
   *
   * @param {string} catalogSku  — product SKU from customMA.akeneoProductId metafield
   * @param {number} page        — 1-based page number
   * @param {number} size        — reviews per page
   * @param {Object} [opts]      — optional overrides
   * @param {string} [opts.country]  — 3-letter country code  (default: USA)
   * @param {string} [opts.language] — 3-letter language code (default: ENG)
   * @param {string} [opts.sort]     — NEWEST|OLDEST|RATING_HI_LO|RATING_LO_HI|HELPFUL|IMAGE (default: NEWEST)
   */
  function apiFetchReviews(catalogSku, page, size, opts) {
    opts = opts || {};
    const params = ["page=" + page, "size=" + size];
    if (opts.country) params.push("country=" + opts.country);
    if (opts.language) params.push("language=" + opts.language);
    if (opts.sort) params.push("sort=" + opts.sort);
    if (opts.rating) params.push("rating=" + opts.rating);
    if (SHOPPER_CID) params.push("shopperCid=" + encodeURIComponent(SHOPPER_CID));

    return fetch(API_BASE + "/" + encodeURIComponent(catalogSku) + "?" + params.join("&"), {
      method: "GET",
      headers: Object.assign({
        Accept: "application/json",
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
        "If-Modified-Since": "0",
      }, amosHeaders()),
    })
      .then(function (r) {
        if (!r.ok) throw new Error(t('loadError', 'Failed to load reviews'));
        return r.json();
      })
      .then(function (data) {
        var reviews    = data.productReviews || [];
        var ratingData = data.ratingData    || {};
        return {
          reviews: reviews,
          total: data.total || ratingData.total || reviews.length,
          average: ratingData.average || 0,
          distribution: (ratingData.counts || []).slice(1),
        };
      });
  }

  /**
   * Mark a review as helpful via the v2 API.
   *
   * @param {string} reviewId   — unique review identifier
   * @returns {Promise<Object>} — resolves with the API response
   */
  function apiMarkHelpful(reviewId) {
    var url = "/shopify-app-proxy/product-reviews/v2/Store/Offer/Reviews/Helpful/" + encodeURIComponent(reviewId) + "/" + encodeURIComponent(SHOPPER_CID);
    return fetch(url, {
      method: "POST",
      headers: Object.assign({
        Accept: "application/json",
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
        "If-Modified-Since": "0",
      }, amosHeaders()),
    }).then(function (r) {
      var status = r.status;
      if (!r.ok) throw new Error(t('helpfulError', 'Failed to mark review as helpful'));
      return r.text().then(function (text) {
        var data = null;
        try { data = JSON.parse(text); } catch (e) { /* response may not be JSON */ }
        return { status: status, data: data };
      });
    });
  }

  /** Success checkmark SVG used in the "Helpful" confirmation message. */
  var HELPFUL_SUCCESS_SVG = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none"'
    + ' xmlns="http://www.w3.org/2000/svg" style="margin-right:3px;vertical-align:middle;flex-shrink:0">'
    + '<circle cx="12" cy="12" r="10" fill="#065f46"/>'
    + '<path d="M8 12.5l2.5 2.5L16 9.5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>'
    + '</svg>';

  /** Max image size before client-side compression kicks in (2 MB). */
  var IMAGE_COMPRESS_THRESHOLD = 2 * 1024 * 1024;
  /** Longest edge (px) a compressed image is scaled down to. */
  var IMAGE_MAX_DIMENSION = 2048;

  /**
   *
   * @param {File} file — image File from an <input type="file">
   * @returns {Promise<File|Blob>} — resolves with the original or compressed file
   */
  function compressImage(file) {
    if (!file || file.size <= IMAGE_COMPRESS_THRESHOLD) return Promise.resolve(file);
    if (/image\/(gif|svg\+xml)/i.test(file.type)) return Promise.resolve(file);
    if (!window.HTMLCanvasElement || !window.URL || !URL.createObjectURL) return Promise.resolve(file);

    return new Promise(function (resolve) {
      var objectUrl = URL.createObjectURL(file);
      var img = new Image();

      img.onerror = function () {
        URL.revokeObjectURL(objectUrl);
        resolve(file); /* can't decode — upload the original */
      };

      img.onload = function () {
        URL.revokeObjectURL(objectUrl);

        var width = img.naturalWidth || img.width;
        var height = img.naturalHeight || img.height;
        var scale = Math.min(1, IMAGE_MAX_DIMENSION / Math.max(width, height));
        var quality = 0.85;
        var MIN_QUALITY = 0.5;
        var attempts = 0;
        var canvas = document.createElement('canvas');
        var ctx = canvas.getContext('2d');

        var baseName = (file.name || 'image').replace(/\.[^.]+$/, '');
        var outName = baseName + '.jpg';

        function toBlob(cb) {
          if (canvas.toBlob) {
            canvas.toBlob(cb, 'image/jpeg', quality);
          } else {
            /* Fallback for browsers without canvas.toBlob */
            var dataUrl = canvas.toDataURL('image/jpeg', quality);
            var bin = atob(dataUrl.split(',')[1]);
            var arr = new Uint8Array(bin.length);
            for (var i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
            cb(new Blob([arr], { type: 'image/jpeg' }));
          }
        }

        function render() {
          canvas.width = Math.max(1, Math.round(width * scale));
          canvas.height = Math.max(1, Math.round(height * scale));
          /* White background so transparent PNG/WebP areas don't turn black */
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        }

        function attempt() {
          attempts++;
          toBlob(function (blob) {
            if (!blob) { resolve(file); return; }

            if (blob.size <= IMAGE_COMPRESS_THRESHOLD || attempts >= 10) {
              /* Never upload something bigger than the original */
              if (blob.size >= file.size) { resolve(file); return; }
              var out;
              try {
                out = new File([blob], outName, { type: 'image/jpeg', lastModified: Date.now() });
              } catch (e) {
                out = blob; /* older browsers without the File constructor */
                out.name = outName;
              }
              resolve(out);
              return;
            }

            if (quality - 0.1 >= MIN_QUALITY) {
              quality -= 0.1;
            } else {
              scale *= 0.8;
              render();
            }
            attempt();
          });
        }

        render();
        attempt();
      };

      img.src = objectUrl;
    });
  }

  /**
   * Upload a single image to the image-uploader API.
   * Compresses the image first if it is larger than 2 MB, then reads it as a
   * base64 data-URI and POSTs it as text/plain.
   *
   * @param {File} file — image File from an <input type="file">
   * @returns {Promise<string>} — resolves with the uploaded image URL
   */
  function apiUploadImage(file) {
    return compressImage(file).then(function (uploadFile) {
      return uploadImageFile(uploadFile);
    });
  }

  /**
   * Read an (already compressed) image as base64 and POST it to the uploader.
   *
   * @param {File|Blob} file
   * @returns {Promise<string>}
   */
  function uploadImageFile(file) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onerror = function () { reject(new Error(t('fileReadError', 'Failed to read file.'))); };
      reader.onload = function () {
        var base64Data = reader.result; /* data:image/jpeg;base64,... */
        var url = IMAGE_UPLOAD_BASE
          + "?imagename=" + encodeURIComponent(file.name || "image.jpg")
          + "&api_key=" + IMAGE_API_KEY;

        var authToken = getApimToken();
        var headers = Object.assign({ "Content-Type": "text/plain" }, amosHeaders());
        if (authToken) headers["Authorization"] = "Bearer " + authToken;

        fetch(url, {
          method: "POST",
          headers: headers,
          body: base64Data
        })
          .then(function (r) {
            if (!r.ok) throw new Error("Image upload failed (" + r.status + ")");
            return r.text();
          })
          .then(function (responseText) {
            /* Try to parse as JSON; fall back to raw text as URL */
            try {
              var json = JSON.parse(responseText);
              resolve(json.url || json.imageUrl || json.data || responseText);
            } catch (e) {
              resolve(responseText.trim());
            }
          })
          .catch(reject);
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * Upload a video to the video upload API.
   * Reads the File as a base64 data-URI, URL-encodes it, and POSTs as text/plain.
   *
   * @param {File} file — video File from an <input type="file">
   * @returns {Promise<string>} — resolves with the uploaded video URL / response
   */
  function apiUploadVideo(file) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onerror = function () { reject(new Error(t('videoReadError', 'Failed to read video file.'))); };
      reader.onload = function () {
        var base64Data = reader.result; /* data:video/mp4;base64,... */
        var encodedBody = encodeURIComponent(base64Data);
        var authToken = getApimToken();
        var headers = Object.assign({ "Content-Type": "text/plain" }, amosHeaders());
        if (authToken) headers["Authorization"] = "Bearer " + authToken;

        fetch(VIDEO_UPLOAD_URL, {
          method: "POST",
          headers: headers,
          body: encodedBody
        })
          .then(function (r) {
            if (!r.ok) throw new Error("Video upload failed (" + r.status + ")");
            return r.text();
          })
          .then(function (responseText) {
            try {
              var json = JSON.parse(responseText);
              resolve(json.url || json.videoUrl || json.data || responseText);
            } catch (e) {
              resolve(responseText.trim());
            }
          })
          .catch(reject);
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * Submit a review via the v2 POST API.
   *
   * @param {Object} data       — form data from collectFormData()
   * @param {string} catalogSku — product SKU (e.g. "5819825usa")
   */
  function apiPostReview(data, catalogSku) {
    /* Client-side validation */
    if (!data.rating) return Promise.reject(new Error(t('validationRating', 'Please select a star rating.')));
    if (!data.author) return Promise.reject(new Error(t('validationName', 'Please enter your name.')));
    if (!data.email)  return Promise.reject(new Error(t('validationEmail', 'Please enter your email address.')));
    if (!data.title)  return Promise.reject(new Error(t('validationTitle', 'Please enter a review title.')));
    if (!data.body)   return Promise.reject(new Error(t('validationBody', 'Please write a review.')));

    const addtionalHeaders = amosHeaders();
    var url = API_BASE + "/" + encodeURIComponent(SHOPPER_CID) + "/" + encodeURIComponent(catalogSku) + 
      "?country=" + addtionalHeaders["AMOS-Country"] + "&language=" + addtionalHeaders["AMOS-Language"];

    var payload = {
      anonymous:           data.anonymous || false,
      heading:             data.title || '',
      rating:              data.rating,
      review:              data.body,
      urlPostedFrom:       window.location.href,
      verifiedBuyerReview: new URLSearchParams(window.location.search).get('ref') === 'review_email',
      reviewImages:        data.reviewImages || [],
      reviewVideoUrl:      data.reviewVideoUrl || ''
    };

    return fetch(url, {
      method:  'POST',
     headers: Object.assign({
        Accept: "application/json",
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
        "If-Modified-Since": "0",
      }, addtionalHeaders),
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) {
        return r.json().catch(function () { return {}; }).then(function (body) {
          throw new Error(body.message || t('submitError', 'Something went wrong. Please try again.'));
        });
      }
      return r.json();
    }).then(function (body) {
      if (body.code && body.code >= 400) {
        let msg = body.message || t('submitError', 'Something went wrong. Please try again.');
        msg = msg.replace(/\b\w/g, function (c) { return c.toUpperCase(); });
        throw new Error(msg);
      }
      return body;
    });
  }

  /* ── Star picker (shared between widget + popup) ─────────────────────────── */

  function initStarPicker(container, onSelect) {
    var picker  = container.querySelector('.pr-star-picker');
    var hidden  = container.querySelector('[name="rating"]');
    var current = 0;

    if (!picker || !hidden) return;

    function paint(hoverVal) {
      picker.querySelectorAll('.pr-star-picker__star').forEach(function (s) {
        var v = parseInt(s.dataset.value, 10);
        s.textContent = v <= (hoverVal || current) ? '★' : '☆';
        s.classList.toggle('is-active', v <= current);
        s.classList.toggle('is-hover',  v <= hoverVal && hoverVal > 0);
      });
    }

    picker.addEventListener('mouseover', function (e) {
      if (e.target.classList.contains('pr-star-picker__star'))
        paint(parseInt(e.target.dataset.value, 10));
    });
    picker.addEventListener('mouseout', function () { paint(0); });
    picker.addEventListener('click', function (e) {
      if (e.target.classList.contains('pr-star-picker__star')) {
        current        = parseInt(e.target.dataset.value, 10);
        hidden.value   = current;
        paint(0);
        if (onSelect) onSelect(current);
      }
    });
    /* keyboard support */
    picker.querySelectorAll('.pr-star-picker__star').forEach(function (s) {
      s.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          s.click();
        }
      });
    });

    return {
      reset: function () { current = 0; hidden.value = 0; paint(0); }
    };
  }

  /* ── Form helper ─────────────────────────────────────────────────────────── */

  function collectFormData(form, productId) {
    var anonCheckbox = form.querySelector('[name="anonymous"]');
    return {
      product_id:   productId,
      // Author/email aren't editable form fields — they come straight from the signed-in
      // customer (see data-customer-name/-email on the <form>, set server-side in Liquid).
      author:       (form.dataset.customerName  || '').trim(),
      email:        (form.dataset.customerEmail || '').trim(),
      title:        (form.querySelector('[name="title"]').value    || '').trim(),
      body:         (form.querySelector('[name="body"]').value     || '').trim(),
      rating:       parseInt(form.querySelector('[name="rating"]').value, 10) || 0,
      anonymous:    anonCheckbox ? anonCheckbox.checked : false,
      reviewImages: []
    };
  }

  function setMsg(msgEl, type, text) {
    msgEl.className = 'pr-form__msg' + (type ? ' is-' + type : '');
    msgEl.textContent = text;
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     1. PRODUCT PAGE REVIEW WIDGET
     ═════════════════════════════════════════════════════════════════════════ */

  function ProductReviewWidget(container) {
    var productId  = container.dataset.productId;
    var perPage    = parseInt(container.dataset.perPage, 10) || 10;
    var page       = 1;
    var summarySet = false;
     const catalogSku = container.dataset.catalogSku;
      if (!catalogSku) {
      console.warn("[product-reviews] No catalogSku found — reviews cannot load.");
      return;
    }
    var summaryEl    = container.querySelector('.pr-summary');
    var listEl       = container.querySelector('.pr-list');
    var paginationEl = container.querySelector('.pr-pagination');
    var formWrapper  = container.querySelector('.pr-form-wrapper');
    var sortSelect   = container.querySelector('.pr-sort-select');
    var currentSort  = sortSelect ? sortSelect.value : 'NEWEST';
    var currentStarFilter = null;   // null = show all, 1–5 = show only that star
    var cachedDistribution = null;  // cached distribution for star-filter totals
    // Tag-qualified: only the real "Write a Review" button (logged-in state) should ever
    // open the form. The logged-out "Login to Review" control is an <a>, not a <button>,
    // so it's never matched here and never triggers the form-toggle listener below.
    var writeBtn     = container.querySelector('button.pr-write-btn');
    var isLoggedIn   = !!writeBtn;
    var form         = container.querySelector('.pr-review-form');
    var submitBtn    = form.querySelector('.pr-form__submit');
    var cancelBtn    = form.querySelector('.pr-form__cancel');
    var msgEl        = form.querySelector('.pr-form__msg');

    var starPicker = initStarPicker(form);

    /* ── Summary ────────────────────────────────────────────────────────── */
    function renderSummary(data) {
      if (summarySet) return;
      summarySet = true;
      var avg   = data.average;
      var total = data.total;
      var dist  = data.distribution;
      cachedDistribution = dist;

      if (!total) {
        var summaryRow = container.querySelector('.pr-summary-row');
        if (summaryRow) summaryRow.style.display = 'none';
        container.querySelector('.pr-widget__heading').style.display = 'none';
        if (writeBtn) writeBtn.style.display = 'none';
        hasNoReviews = true;
        return;
      }

      summaryEl.innerHTML =
        '<div class="pr-summary__score">'
        + '<div class="pr-summary__score-number">' + avg.toFixed(1) + '</div>'
        + renderStars(Math.round(avg))
        + '<div class="pr-summary__score-label">' + (total !== 1 ? tReplace('reviewCountPlural', {count: total}, total + ' reviews') : tReplace('reviewCount', {count: total}, total + ' review')) + '</div>'
        + '</div>'
        + '<div class="pr-summary__bars">'
        + [5, 4, 3, 2, 1].map(function (star) {
            var count = dist[star - 1] || 0;
            var pct   = total > 0 ? (count / total) * 100 : 0;
            var isActive = currentStarFilter === star;
            return '<div class="pr-summary__bar-row' + (isActive ? ' is-active' : '') + '"'
              + ' data-star="' + star + '"'
              + ' role="button" tabindex="0"'
              + ' aria-label="' + tReplace('starFilterAria', {star: star, count: count}, 'Filter by ' + star + ' star reviews (' + count + ')') + '"'
              + ' style="cursor:pointer">'
              + '<span class="pr-summary__bar-label">' + star + '★</span>'
              + '<div class="pr-summary__bar-track"><div class="pr-summary__bar-fill" style="width:' + pct + '%"></div></div>'
              + '<span class="pr-summary__bar-count">' + count + '</span>'
              + '</div>';
          }).join('')
        + '</div>';

      var recommendEl = container.querySelector('.pr-recommend');
      if (recommendEl && total > 0) {
        var fiveStarPct = Math.round(((dist[4] || 0) / total) * 100);
        var fourStarPct = Math.round(((dist[3] || 0) / total) * 100);
        var recommendPct = fiveStarPct + fourStarPct;
        recommendEl.textContent = tReplace('recommendPct', {percent: recommendPct}, recommendPct + '% would recommend this product');
      }

      /* ── Star-filter click handlers (event delegation on summary) ───── */
      summaryEl.querySelectorAll('.pr-summary__bar-row[data-star]').forEach(function (row) {
        row.addEventListener('click', function () {
          var star = parseInt(row.getAttribute('data-star'), 10);
          // Toggle: clicking the same star again clears the filter
          currentStarFilter = (currentStarFilter === star) ? null : star;
          page = 1;
          loadPage(1);
        });
        row.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            row.click();
          }
        });
      });
    }

    /* ── Review list ────────────────────────────────────────────────────── */
    function renderSkeleton() {
      listEl.innerHTML = '<div class="pr-skeleton">'
        + '<div class="pr-skeleton__item"></div>'
        + '<div class="pr-skeleton__item"></div>'
        + '<div class="pr-skeleton__item"></div>'
        + '</div>';
    }

    function renderReviews(reviews, total) {
      if (!reviews.length) {
        if (total > 0) {
          listEl.innerHTML = '';
          renderPagination(total);
          return;
        }
        paginationEl.innerHTML = '';
        if (hasNoReviews) {
          var firstReviewBtn;
          if (isLoggedIn) {
            var firstReviewButtonEl = document.createElement('button');
            firstReviewButtonEl.type = 'button';
            firstReviewButtonEl.addEventListener('click', function () {
              formWrapper.classList.add('is-open');
              firstReviewButtonEl.style.display = 'none';
              focusFirstFormField(formWrapper);
            });
            firstReviewBtn = firstReviewButtonEl;
          } else {
            var firstReviewLinkEl = document.createElement('a');
            firstReviewLinkEl.href = (typeof Theme !== 'undefined' && Theme.routes.storefront_login_url) || '/account/login';
            firstReviewLinkEl.addEventListener('click', function () {
              sessionStorage.setItem('pr-scroll-pending', '1');
            });
            firstReviewBtn = firstReviewLinkEl;
          }
          firstReviewBtn.className = 'pr-write-btn pr-write-btn--first';
          firstReviewBtn.innerHTML = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none"'
            + ' stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"'
            + ' aria-hidden="true">'
            + '<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>'
            + '<path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>'
            + '</svg> ' + (isLoggedIn ? t('firstReviewLoggedIn', 'Be the first to write a review') : t('firstReviewLoggedOut', 'Login to be the first to write a review'));
          listEl.innerHTML = '';
          listEl.appendChild(firstReviewBtn);
        } else {
          listEl.innerHTML = '';
        }
        return;
      }

      listEl.innerHTML = reviews.map(function (r) {
        var reviewId = r.id || r.reviewId || '';
        var isHelpful = r.helpful === true;
        var helpfulCount = r.helpfulCount || 0;

        return '<article class="pr-card">'
          + '<div class="pr-card__header">'
          + '<div class="pr-card__meta">'
          + renderStars(r.rating)
          + '</div>'
          + '<time class="pr-card__date" datetime="' + r.when + '">' + r.when + '</time>'
          + '</div>'
          + (r.heading ? '<p class="pr-card__title">' + r.heading + '</p>' : '')
          + '<p class="pr-card__body">' + r.review + '</p>'
          + (r.reviewReply
              ? '<div class="pr-card__reply">'
                + '<p class="pr-card__reply-header"><strong>' + t('replyHeader', 'Response from Customer Service') + '</strong>'
                + (r.replyDate ? t('replyDatePrefix', ' on ') + r.replyDate : '')
                + '</p>'
                + '<p class="pr-card__reply-body">' + r.reviewReply + '</p>'
                + '</div>'
              : '')
          + '<div class="pr-card__meta pr-card__meta--footer">'
          + '<span class="pr-card__author">' + (r.shopperName || t('anonymousAuthor', 'by Anonymous')) + '</span>'
          + (r.consultant ? '<span class="pr-card__verified pr-card__verified--employee">' + t('shopConsultant', 'Shop Consultant') + '</span>' : '')
          + (r.verifiedBuyer ? '<span class="pr-card__verified pr-card__verified--buyer">' + t('verifiedBuyer', 'Verified Buyer') + '</span>' : '')
          + (r.staffReviewer ? '<span class="pr-card__verified pr-card__verified--employee">' + t('employeeBadge', 'Employee') + '</span>' : '')
          + '</div>'
          + renderReviewMedia(r)
          + '<div class="pr-card__helpful" data-review-id="' + reviewId + '" data-helpful-count="' + helpfulCount + '">'
          + (isHelpful
              ? '<button class="pr-card__helpful-btn" disabled>' + t('helpfulBtn', 'Helpful') + '</button>'
              : '<button class="pr-card__helpful-btn">' + t('helpfulBtn', 'Helpful') + '</button>')
          + (helpfulCount > 0 ? '<span class="pr-card__helpful-msg">' + HELPFUL_SUCCESS_SVG + t('shoppersFoundHelpful', 'Shoppers found this review helpful') + '</span>' : '')
          + '</div>'
          + '</article>';
      }).join('');

      /* ── Attach Helpful button click handlers ─────────────────────────── */
      listEl.querySelectorAll('.pr-card__helpful').forEach(function (helpfulDiv) {
        var btn = helpfulDiv.querySelector('.pr-card__helpful-btn');
        if (!btn || btn.disabled) return;

        btn.addEventListener('click', function () {
          if (!SHOPPER_CID) {
            var loginUrl = (typeof Theme !== 'undefined' && Theme.routes.storefront_login_url) || '/account/login';
            window.location.href = loginUrl;
            return;
          }
          var reviewId = helpfulDiv.getAttribute('data-review-id');
          var currentCount = parseInt(helpfulDiv.getAttribute('data-helpful-count'), 10) || 0;
          btn.disabled = true;
          btn.textContent = t('helpfulSending', 'Sending…');

          apiMarkHelpful(reviewId)
            .then(function (result) {
              if (result.status === 200) {
                var newCount = currentCount + 1;
                helpfulDiv.setAttribute('data-helpful-count', newCount);
                // Insert success message after the button
                var msgSpan = document.createElement('span');
                msgSpan.className = 'pr-card__helpful-msg';
                msgSpan.innerHTML = HELPFUL_SUCCESS_SVG + t('helpfulMessage', 'Shoppers found this review helpful');
                btn.insertAdjacentElement('afterend', msgSpan);
                btn.textContent = t('helpfulBtn', 'Helpful');
                btn.disabled = true;
              } else {
                btn.disabled = false;
                btn.textContent = t('helpfulBtn', 'Helpful');
              }
            })
            .catch(function () {
              btn.disabled = false;
              btn.textContent = t('helpfulBtn', 'Helpful');
            });
        });
      });

      renderPagination(total);
    }

    function renderPagination(total) {
      var pages = Math.ceil(total / perPage);
      paginationEl.innerHTML = '';
      if (pages <= 1) return;

      function btn(label, targetPage, disabled, active) {
        var b = document.createElement('button');
        b.className = 'pr-pagination__btn' + (active ? ' is-active' : '');
        b.textContent = label;
        b.disabled = disabled;
        b.setAttribute('aria-label', tReplace('pageAria', {page: targetPage}, 'Page ' + targetPage));
        if (active) b.setAttribute('aria-current', 'page');
        if (!disabled) b.addEventListener('click', function () { loadPage(targetPage); });
        paginationEl.appendChild(b);
      }

      var windowSize  = 10;
      var windowStart = Math.floor((page - 1) / windowSize) * windowSize + 1;
      var windowEnd   = Math.min(windowStart + windowSize - 1, pages);

      btn(t('prevPage', '← Prev'), page - 1, page === 1,     false);
      for (var p = windowStart; p <= windowEnd; p++) btn(p, p, false, p === page);
      btn(t('nextPage', 'Next →'), page + 1, page === pages, false);
    }

    var isInitialLoad = true;
    var hasNoReviews = false;

    function loadPage(p) {
      page = p;
      renderSkeleton();

      var amos = amosHeaders();
      var fetchOpts = {
        country: amos['AMOS-Country'],
        language: amos['AMOS-Language'],
        sort: currentSort,
      };
      // When a star filter is active, request more reviews so client-side
      // filtering still fills a page, and pass the rating param in case the
      // API supports server-side filtering.
      var fetchSize = perPage;
      if (currentStarFilter) {
        fetchOpts.rating = currentStarFilter;
        fetchSize = Math.max(perPage * 5, 50); // fetch extra to compensate for filtering
      }

      apiFetchReviews(catalogSku, currentStarFilter ? 0 : p, fetchSize, fetchOpts)
        .then(function (data) {
          // Always render the summary with full (unfiltered) data
          renderSummary(data);

          var reviewsToShow = data.reviews;
          var totalToUse    = data.total;

          if (currentStarFilter) {
            // Client-side filter (in case the API returned mixed ratings)
            reviewsToShow = data.reviews.filter(function (r) {
              return r.rating === currentStarFilter;
            });

            // Use the distribution count for the selected star as the total
            totalToUse = (cachedDistribution && cachedDistribution[currentStarFilter - 1]) || reviewsToShow.length;

            // Local pagination over the filtered set
            var start = (p - 1) * perPage;
            reviewsToShow = reviewsToShow.slice(start, start + perPage);

            // Show an active-filter indicator above the review list
            var filterNotice = container.querySelector('.pr-filter-notice');
            if (!filterNotice) {
              filterNotice = document.createElement('div');
              filterNotice.className = 'pr-filter-notice';
              listEl.parentNode.insertBefore(filterNotice, listEl);
            }
            filterNotice.innerHTML = tReplace('showingFilter', {star: currentStarFilter}, 'Showing ' + currentStarFilter + '★ reviews')
              + ' <button class="pr-filter-notice__clear" aria-label="' + escHtml(t('clearFilter', 'Clear')) + '">✕ ' + escHtml(t('clearFilter', 'Clear')) + '</button>';
            filterNotice.querySelector('.pr-filter-notice__clear').addEventListener('click', function () {
              currentStarFilter = null;
              loadPage(1);
            });
            filterNotice.style.display = '';
          } else {
            // Remove filter notice when no filter is active
            var existingNotice = container.querySelector('.pr-filter-notice');
            if (existingNotice) existingNotice.style.display = 'none';
          }

          renderReviews(reviewsToShow, totalToUse);
          if (!isInitialLoad) {
            container.scrollIntoView({ behavior: "smooth", block: "start" });
          }
          isInitialLoad = false;

          /* Broadcast rating data so review-star widgets can consume it without a separate fetch */
          if (p === 1 && !currentStarFilter) {
            document.dispatchEvent(
              new CustomEvent("product-reviews:loaded", {
                detail: { catalogSku: catalogSku, average: data.average, total: data.total, distribution: data.distribution },
              })
            );
          }
        })
        .catch(function () {
          listEl.innerHTML = '<p class="pr-list__empty">' + t('loadReviewsError', 'Could not load reviews. Please refresh the page.') + '</p>';
        });
    }

    /* ── Form toggle ────────────────────────────────────────────────────── */
    // writeBtn only exists for signed-in customers (see the tag-qualified selector above) —
    // guests only ever see the "Login to Review" <a>, which has no toggle behavior of its own.
    if (writeBtn) {
      writeBtn.addEventListener('click', function () {
        formWrapper.classList.add('is-open');
        writeBtn.setAttribute('aria-expanded', 'true');
        writeBtn.style.display = 'none';
        focusFirstFormField(formWrapper);
      });
    }

    cancelBtn.addEventListener('click', function () {
      formWrapper.classList.remove('is-open');
      if (hasNoReviews) {
        const firstBtn = listEl.querySelector('.pr-write-btn--first');
        if (firstBtn) firstBtn.style.display = 'flex';
      } else if (writeBtn) {
        writeBtn.style.display = '';
      }
      if (writeBtn) writeBtn.setAttribute('aria-expanded', 'false');
      form.reset();
      if (starPicker) starPicker.reset();
      resetMediaPreviews(form);
      setMsg(msgEl, '', '');
    });

    /* ── Form submit ────────────────────────────────────────────────────── */
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      setMsg(msgEl, '', '');

      var data = collectFormData(form, productId);
      var media = getUploadedMedia(form);
      data.reviewImages   = media.reviewImages;
      data.reviewVideoUrl = media.reviewVideoUrl;
      submitBtn.disabled    = true;
      submitBtn.textContent = t('submitting', 'Submitting…');

      apiPostReview(data, catalogSku)
        .then(function () {
          reportFormSubmit(form);
          setMsg(msgEl, 'success', t('submitSuccess', 'Thank you! Your review has been submitted and is pending approval.'));
          form.reset();
          if (starPicker) starPicker.reset();
          resetMediaPreviews(form);
          /* Reload reviews to show the new one */
          loadPage(1);
        })
        .catch(function (err) {
          setMsg(msgEl, 'error', err.message || t('submitError', 'Something went wrong. Please try again.'));
        })
        .finally(function () {
          submitBtn.disabled    = false;
          submitBtn.textContent = t('submit', 'Submit Review');
        });
    });

    /* ── Review image lightbox (delegated on list container) ──────────── */
    listEl.addEventListener('click', function (e) {
      var btn = e.target.closest('.pr-card__media-link[data-full-url]');
      if (!btn) return;
      e.preventDefault();
      /* Collect all image URLs within the same review card */
      var card = btn.closest('.pr-card');
      var btns = card ? card.querySelectorAll('.pr-card__media-link[data-full-url]') : [btn];
      var urls = [];
      btns.forEach(function (b) { urls.push(b.dataset.fullUrl); });
      var idx = parseInt(btn.dataset.reviewImgIndex, 10) || 0;
      openLightbox(urls, idx);
    });

    /* ── Sort ───────────────────────────────────────────────────────────── */
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        currentSort = sortSelect.value;
        loadPage(1);
      });
    }

    /* ── Boot ───────────────────────────────────────────────────────────── */
    loadPage(1);
    initImageGallery(container, catalogSku);
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     2. POST-PURCHASE REVIEW POPUP
     ═════════════════════════════════════════════════════════════════════════ */

  function PostPurchasePopup(overlay) {
    var productId = overlay.dataset.productId;
    var popup     = overlay.querySelector('.pr-popup');
    var closeBtn  = overlay.querySelector('.pr-popup__close');
    var laterBtn  = overlay.querySelector('.pr-popup__later');
    var form      = overlay.querySelector('.pr-review-form');
    var submitBtn = form.querySelector('.pr-form__submit');
    var msgEl     = form.querySelector('.pr-form__msg');

    var STORAGE_KEY = 'pr_dismissed_' + productId;
    var SNOOZE_MS   = 30 * 24 * 60 * 60 * 1000; /* 30 days */

    /* Don't show if dismissed recently */
    try {
      var ts = localStorage.getItem(STORAGE_KEY);
      if (ts && Date.now() - parseInt(ts, 10) < SNOOZE_MS) return;
    } catch (e) {}

    var starPicker = initStarPicker(form);

    function open()  { overlay.classList.add('is-open');    overlay.querySelector('.pr-popup__close').focus(); }
    function close() {
      overlay.classList.remove('is-open');
      try { localStorage.setItem(STORAGE_KEY, Date.now()); } catch (e) {}
    }

    /* Show after a short delay so the page settles first */
    setTimeout(open, 1800);

    closeBtn.addEventListener('click', close);
    laterBtn.addEventListener('click', close);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
    });

    /* ── Form submit ────────────────────────────────────────────────────── */
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      setMsg(msgEl, '', '');

      var data      = collectFormData(form, productId);
      submitBtn.disabled    = true;
      submitBtn.textContent = t('submitting', 'Submitting…');

      apiPostReview(data)
        .then(function () {
          reportFormSubmit(form);
          popup.innerHTML =
            '<div class="pr-popup__success">'
            + '<div class="pr-popup__success-icon">&#127881;</div>'
            + '<h3 class="pr-popup__success-heading">Thank you for your review!</h3>'
            + '<p class="pr-popup__success-body">Your feedback helps other shoppers make better choices.</p>'
            + '<button class="pr-form__submit pr-popup__done-btn" style="margin:0 auto;display:flex" type="button">Close</button>'
            + '</div>';
          popup.querySelector('.pr-popup__done-btn').addEventListener('click', close);
          try { localStorage.setItem(STORAGE_KEY, Date.now()); } catch (e) {}
        })
        .catch(function (err) {
          setMsg(msgEl, 'error', err.message || t('submitError', 'Something went wrong. Please try again.'));
          submitBtn.disabled    = false;
          submitBtn.textContent = t('submit', 'Submit Review');
        });
    });
  }

  /* ── Image gallery lightbox ──────────────────────────────────────────────── */

  var lightboxOverlay = null;

  /** Shared lightbox state so keyboard handler can access current images/index */
  var lbState = { images: [], idx: 0 };

  function openLightbox(images, startIndex) {
    lbState.images = images;
    lbState.idx = startIndex || 0;

    if (!lightboxOverlay) {
      lightboxOverlay = document.createElement('div');
      lightboxOverlay.className = 'pr-lightbox-overlay';
      lightboxOverlay.innerHTML =
        '<div class="pr-lightbox">'
        + '<button class="pr-lightbox__close" aria-label="' + t('lightboxClose', 'Close') + '">&times;</button>'
        + '<button class="pr-lightbox__prev" aria-label="' + t('lightboxPrev', 'Previous') + '">&#8249;</button>'
        + '<img class="pr-lightbox__img" alt="' + escHtml(t('reviewImageAlt', 'Review image')) + '" />'
        + '<button class="pr-lightbox__next" aria-label="' + t('lightboxNext', 'Next') + '">&#8250;</button>'
        + '</div>';
      document.body.appendChild(lightboxOverlay);

      lightboxOverlay.querySelector('.pr-lightbox__close').addEventListener('click', closeLightbox);
      lightboxOverlay.addEventListener('click', function (e) { if (e.target === lightboxOverlay) closeLightbox(); });
      lightboxOverlay.querySelector('.pr-lightbox__prev').addEventListener('click', function () { lbNavigate(-1); });
      lightboxOverlay.querySelector('.pr-lightbox__next').addEventListener('click', function () { lbNavigate(1); });
      document.addEventListener('keydown', function (e) {
        if (!lightboxOverlay.classList.contains('is-open')) return;
        if (e.key === 'Escape') closeLightbox();
        if (e.key === 'ArrowLeft') lbNavigate(-1);
        if (e.key === 'ArrowRight') lbNavigate(1);
      });
    }

    lbShow();
    lightboxOverlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function lbShow() {
    var imgEl = lightboxOverlay.querySelector('.pr-lightbox__img');
    var prevBtn = lightboxOverlay.querySelector('.pr-lightbox__prev');
    var nextBtn = lightboxOverlay.querySelector('.pr-lightbox__next');
    imgEl.src = lbState.images[lbState.idx];
    prevBtn.style.display = lbState.images.length > 1 ? '' : 'none';
    nextBtn.style.display = lbState.images.length > 1 ? '' : 'none';
  }

  function lbNavigate(dir) {
    lbState.idx = (lbState.idx + dir + lbState.images.length) % lbState.images.length;
    lbShow();
  }

  function closeLightbox() {
    if (lightboxOverlay) {
      lightboxOverlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  }

  /* ── Review image gallery (IMAGE-sorted endpoint) ──────────────────────── */

  function initImageGallery(container, catalogSku) {
    var galleryEl = container.querySelector('.pr-image-gallery');
    if (!galleryEl) return;
    /* Show skeleton while loading */
    galleryEl.innerHTML =
      '<div class="pr-image-gallery__skeleton">'
      + '<div class="pr-image-gallery__skel-item"></div>'
      + '<div class="pr-image-gallery__skel-item"></div>'
      + '<div class="pr-image-gallery__skel-item"></div>'
      + '<div class="pr-image-gallery__skel-item"></div>'
      + '<div class="pr-image-gallery__skel-item"></div>'
      + '</div>';

    apiFetchReviews(catalogSku, 0, 5, { country: amosHeaders()['AMOS-Country'], language: amosHeaders()['AMOS-Language'], sort: 'IMAGE' })
      .then(function (data) {
        /* Extract all images from the returned reviews */
        var allImages = [];
        (data.reviews || []).forEach(function (r) {
          (r.reviewImages || []).forEach(function (img) {
            var galleryUrl = img.galleryImageUrl || img.mainImageUrl;
            if (galleryUrl) {
              allImages.push({ thumb: galleryUrl, full: img.mainImageUrl || galleryUrl });
            }
          });
        });

        /* Limit to 5 */
        allImages = allImages.slice(0, 5);

        if (!allImages.length) {
          galleryEl.style.display = 'none';
          return;
        }

        galleryEl.innerHTML = '<div class="pr-image-gallery__grid">'
          + allImages.map(function (img, i) {
              return '<button class="pr-image-gallery__thumb" data-index="' + i + '" type="button" aria-label="' + escHtml(t('galleryView', 'View review photo')) + '">'
                + '<img src="' + escHtml(img.thumb) + '" alt="' + escHtml(t('galleryPhotoAlt', 'Customer review photo')) + '" loading="lazy" />'
                + '</button>';
            }).join('')
          + '</div>';

        var fullUrls = allImages.map(function (img) { return img.full; });

        galleryEl.addEventListener('click', function (e) {
          var btn = e.target.closest('.pr-image-gallery__thumb');
          if (!btn) return;
          var i = parseInt(btn.dataset.index, 10);
          openLightbox(fullUrls, i);
        });
      })
      .catch(function (err) {
        console.warn('Image gallery fetch failed:', err);
        galleryEl.style.display = 'none';
      });
  }

  /* ── Tiny XSS guard ─────────────────────────────────────────────────────── */
  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ── Media preview + upload (image / video thumbnails) ───────────────── */

  /**
   * Stores uploaded media info keyed by the slot's input element.
   * Each value is { type: 'image'|'video', url: string }.
   * Populated by initMediaPreviews, consumed by the form submit handler.
   */
  var uploadedMediaMap = new WeakMap();

  function initMediaPreviews(container) {
    var slots = container.querySelectorAll('.pr-form__media-slot');
    slots.forEach(function (slot) {
      var input = slot.querySelector('.pr-form__media-input');
      if (!input) return;

      input.addEventListener('change', function () {
        var file = input.files && input.files[0];
        if (!file) return;

        var icon = slot.querySelector('.pr-form__media-icon');
        if (icon) icon.style.display = 'none';

        /* Remove any existing preview */
        var old = slot.querySelector('.pr-form__media-preview');
        if (old) old.remove();
        uploadedMediaMap.delete(input);

        var preview = document.createElement('div');
        preview.className = 'pr-form__media-preview';

        /* ── Thumbnail ── */
        if (file.type.startsWith('image/')) {
          var img = document.createElement('img');
          img.className = 'pr-form__media-thumb';
          img.alt = t('previewAlt', 'Preview');
          img.src = URL.createObjectURL(file);
          preview.appendChild(img);
        } else if (file.type.startsWith('video/')) {
          var video = document.createElement('video');
          video.className = 'pr-form__media-thumb';
          video.muted = true;
          video.playsInline = true;
          video.src = URL.createObjectURL(file);
          video.addEventListener('loadeddata', function () { video.currentTime = 0.5; });
          preview.appendChild(video);
        }

        /* ── Upload spinner overlay ── */
        var spinner = document.createElement('div');
        spinner.className = 'pr-form__media-spinner';
        spinner.innerHTML = '<div class="pr-form__media-spinner-ring"></div>';
        preview.appendChild(spinner);

        /* ── Remove button (hidden while uploading) ── */
        var removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'pr-form__media-remove';
        removeBtn.style.display = 'none';
        removeBtn.innerHTML = '&times;';
        removeBtn.setAttribute('aria-label', t('removeFile', 'Remove file'));
        removeBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          input.value = '';
          uploadedMediaMap.delete(input);
          preview.remove();
          if (icon) icon.style.display = '';
        });
        preview.appendChild(removeBtn);

        slot.appendChild(preview);

        /* ── Upload image via API ── */
        if (file.type.startsWith('image/')) {
          apiUploadImage(file)
            .then(function (imageUrl) {
              uploadedMediaMap.set(input, { type: 'image', url: imageUrl });
              spinner.remove();
              removeBtn.style.display = '';
              preview.classList.add('is-uploaded');
            })
            .catch(function (err) {
              console.error('[product-reviews] Image upload failed:', err);
              spinner.remove();
              removeBtn.style.display = '';
              preview.classList.add('is-upload-error');
              var errBadge = document.createElement('span');
              errBadge.className = 'pr-form__media-error';
              errBadge.textContent = t('uploadFailed', 'Upload failed');
              preview.appendChild(errBadge);
            });
        } else if (file.type.startsWith('video/')) {
          /* ── Validate video: max 50 MB ── */
          var MAX_VIDEO_SIZE = 50 * 1024 * 1024; /* 50 MB */
          if (file.size > MAX_VIDEO_SIZE) {
            spinner.remove();
            removeBtn.style.display = '';
            preview.classList.add('is-upload-error');
            var sizeBadge = document.createElement('span');
            sizeBadge.className = 'pr-form__media-error';
            sizeBadge.textContent = t('videoMaxSize', 'Max 50 MB');
            preview.appendChild(sizeBadge);
            input.value = '';
            return;
          }

          /* ── Validate video: max 30 seconds ── */
          var objectUrl = URL.createObjectURL(file);
          var tempVideo = document.createElement('video');
          tempVideo.preload = 'metadata';
          tempVideo.src = objectUrl;
          tempVideo.addEventListener('loadedmetadata', function () {
            URL.revokeObjectURL(objectUrl);
            if (tempVideo.duration > 30) {
              spinner.remove();
              removeBtn.style.display = '';
              preview.classList.add('is-upload-error');
              var durBadge = document.createElement('span');
              durBadge.className = 'pr-form__media-error';
              durBadge.textContent = t('videoMaxDuration', 'Max 30 seconds');
              preview.appendChild(durBadge);
              input.value = '';
              return;
            }

            /* Duration OK — proceed with upload */
            apiUploadVideo(file)
              .then(function (videoUrl) {
                uploadedMediaMap.set(input, { type: 'video', url: videoUrl });
                spinner.remove();
                removeBtn.style.display = '';
                preview.classList.add('is-uploaded');
              })
              .catch(function (err) {
                console.error('[product-reviews] Video upload failed:', err);
                spinner.remove();
                removeBtn.style.display = '';
                preview.classList.add('is-upload-error');
                var errBadge = document.createElement('span');
                errBadge.className = 'pr-form__media-error';
                errBadge.textContent = t('uploadFailed', 'Upload failed');
                preview.appendChild(errBadge);
              });
          });
        }
      });
    });
  }

  /**
   * Collect uploaded media from the form.
   * @returns {{ reviewImages: Array<{imageURL: string}>, reviewVideoUrl: string }}
   */
  function getUploadedMedia(form) {
    var images = [];
    var videoUrl = '';
    form.querySelectorAll('.pr-form__media-input').forEach(function (input) {
      if (uploadedMediaMap.has(input)) {
        var entry = uploadedMediaMap.get(input);
        if (entry.type === 'image') {
          images.push({ imageURL: entry.url });
        } else if (entry.type === 'video') {
          videoUrl = entry.url;
        }
      }
    });
    return { reviewImages: images, reviewVideoUrl: videoUrl };
  }

  function resetMediaPreviews(container) {
    container.querySelectorAll('.pr-form__media-preview').forEach(function (p) { p.remove(); });
    container.querySelectorAll('.pr-form__media-icon').forEach(function (i) { i.style.display = ''; });
    container.querySelectorAll('.pr-form__media-input').forEach(function (input) {
      uploadedMediaMap.delete(input);
    });
  }

  /**
   * Review-request emails link back to the PDP with ?ref=review_email so the
   * page jumps straight to #pr-widget. The param is left in the URL untouched
   * (no history.replaceState) — only the scroll behavior is triggered by it.
   *
   */
  function scrollToReviewsFromEmailLink() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('ref') !== 'review_email') return;

    let scrolled = false;
    function scrollWhenReady() {
      if (scrolled) return;
      const widget = document.getElementById('pr-widget');
      if (!widget) return;
      scrolled = true;
      observer.disconnect();
      widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    var observer = new MutationObserver(scrollWhenReady);
    observer.observe(document.body, { childList: true, subtree: true });
    scrollWhenReady();

    /* Stop watching after 10s so a missing #pr-widget doesn't observe forever. */
    setTimeout(function () { observer.disconnect(); }, 10000);
  }

  /* ── Auto-init ──────────────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    scrollToReviewsFromEmailLink();

    var widget = document.getElementById('pr-widget');

    // Guests see a "Login to Review" link in place of the Write a Review button.
    // Flag intent to come back and write a review before navigating away, so it
    // can be resumed on return — routes.storefront_login_url doesn't preserve
    // URL fragments, so a query/hash target can't carry this across the redirect.
    var loginBtn = document.querySelector('.pr-login-btn');
    if (loginBtn) {
      loginBtn.addEventListener('click', function () {
        sessionStorage.setItem('pr-scroll-pending', '1');
      });
    }

    if (widget) {
      new ProductReviewWidget(widget);
      initMediaPreviews(widget);

      if (sessionStorage.getItem('pr-scroll-pending')) {
        sessionStorage.removeItem('pr-scroll-pending');
        widget.scrollIntoView({ behavior: 'smooth', block: 'start' });

        // Now signed in: skip the extra "Write a Review" click and go straight to the
        // form, focused — this is what the guest was trying to do before being sent to log in.
        var writeBtn = widget.querySelector('button.pr-write-btn');
        if (writeBtn instanceof HTMLButtonElement) {
          writeBtn.click();
        } else {
          widget.setAttribute('tabindex', '-1');
          widget.focus({ preventScroll: true });
        }
      }
    }

    var popup = document.getElementById('pr-popup-overlay');
    if (popup) new PostPurchasePopup(popup);
  });

})();
